<template>
  <div class="flex-1 flex flex-col min-w-0">
    <!-- 聊天头部 -->
    <div class="h-14 px-4 flex items-center justify-between border-b border-slate-200 bg-white flex-shrink-0">
      <div class="flex items-center gap-3 min-w-0">
        <img
          v-if="conversation.type !== 'group' && headerAvatar"
          :src="headerAvatar"
          class="w-8 h-8 rounded-full object-cover flex-shrink-0"
        />
        <div
          v-else-if="conversation.type !== 'group'"
          class="w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0"
          :class="conversation.type === 'internal' ? 'bg-primary-500' : 'bg-emerald-500'"
        >
          {{ conversation.name.slice(-2) }}
        </div>
        <div v-else class="w-8 h-8 rounded-full bg-violet-500 flex items-center justify-center flex-shrink-0">
          <svg class="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"/>
          </svg>
        </div>

        <div class="min-w-0">
          <div class="flex items-center gap-2">
            <h3 class="text-sm font-semibold text-slate-800 truncate">{{ conversation.name }}</h3>
            <span
              v-if="conversation.type === 'internal'"
              class="px-1.5 py-0.5 text-[10px] leading-none rounded bg-primary-100 text-primary-600 flex-shrink-0"
            >内部人员</span>
            <span
              v-else-if="conversation.type === 'customer'"
              class="px-1.5 py-0.5 text-[10px] leading-none rounded bg-emerald-100 text-emerald-600 flex-shrink-0"
            >C端客户</span>
            <span
              v-else-if="conversation.type === 'group'"
              class="px-1.5 py-0.5 text-[10px] leading-none rounded bg-violet-100 text-violet-600 flex-shrink-0"
            >{{ conversation.memberCount }}人</span>
          </div>
          <p v-if="conversation.type !== 'group' && conversation.online" class="text-[11px] text-green-500">在线</p>
          <p v-else-if="conversation.type !== 'group'" class="text-[11px] text-slate-400">离线</p>
        </div>
      </div>

      <div class="flex items-center gap-1">
        <button
          class="p-2 rounded-lg transition-colors cursor-pointer"
          :class="chatStore.messageSearchActive ? 'text-primary-500 bg-primary-50' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'"
          title="搜索聊天记录"
          @click="chatStore.toggleMessageSearch()"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
          </svg>
        </button>
        <button
          class="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
          :class="chatStore.showInfoPanel ? 'text-primary-500 bg-primary-50' : ''"
          title="会话详情"
          @click="chatStore.toggleInfoPanel()"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- 消息搜索栏 -->
    <div v-if="chatStore.messageSearchActive" class="px-4 py-2 bg-white border-b border-slate-100 flex items-center gap-2">
      <div class="relative flex-1">
        <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
        </svg>
        <input
          v-model="chatStore.messageSearchKeyword"
          type="text"
          placeholder="在聊天记录中搜索..."
          class="w-full pl-10 pr-4 py-1.5 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          autofocus
        />
      </div>
      <span v-if="chatStore.messageSearchKeyword" class="text-xs text-slate-500 flex-shrink-0">
        {{ chatStore.messageSearchResults.length }} 条结果
      </span>
      <button
        class="p-1 text-slate-400 hover:text-slate-600 rounded transition-colors cursor-pointer"
        @click="chatStore.toggleMessageSearch()"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>

    <!-- 消息区域 -->
    <div ref="messagesContainer" class="flex-1 overflow-y-auto p-4 space-y-1 bg-slate-50/50 relative" @scroll="handleScroll">
      <!-- 加载状态 -->
      <div v-if="chatStore.messageLoading" class="flex justify-center py-8">
        <div class="flex items-center gap-2 text-slate-400 text-sm">
          <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          加载消息中...
        </div>
      </div>

      <!-- 消息列表 -->
      <template v-else>
        <template v-for="(msg, index) in chatStore.currentMessages" :key="msg.id">
          <!-- 日期分割线 -->
          <div
            v-if="shouldShowDate(msg, index)"
            class="flex items-center justify-center py-2"
          >
            <span class="px-3 py-1 text-[11px] text-slate-400 bg-slate-200/60 rounded-full">
              {{ formatMessageDate(msg.time) }}
            </span>
          </div>

          <!-- 系统消息 -->
          <div v-if="msg.type === 'system'" class="flex justify-center py-1">
            <span class="px-3 py-1 text-[11px] text-slate-400 bg-slate-200/60 rounded-full">
              {{ msg.content }}
            </span>
          </div>

          <!-- 普通消息 -->
          <MessageBubble
            v-else
            :message="msg"
            :is-self="String(msg.senderId) === String(currentUserId) && msg.senderType === 1"
            :show-sender="conversation.type === 'group'"
            :avatar-color="getAvatarColor(msg.senderId, msg.senderType)"
            :conversation-type="conversation.type"
            :members="conversation.members || []"
            :current-user-id="currentUserId"
            :cached-user="chatStore.getUserInfo(msg.senderId)"
            @reply="handleReply"
            @delete="handleDeleteMessage"
          />
        </template>

        <!-- 搜索无结果 -->
        <div v-if="chatStore.messageSearchActive && chatStore.messageSearchKeyword && chatStore.currentMessages.length === 0" class="flex justify-center py-8">
          <p class="text-sm text-slate-400">没有找到相关消息</p>
        </div>
      </template>
    </div>

    <!-- 滚动到底部按钮 -->
    <transition name="fade-up">
      <button
        v-if="showScrollButton"
        class="absolute bottom-28 right-6 w-9 h-9 bg-white shadow-lg border border-slate-200 rounded-full flex items-center justify-center text-slate-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer z-10"
        @click="scrollToBottom"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
        </svg>
      </button>
    </transition>

    <!-- 输入区域 -->
    <ChatInput
      ref="chatInputRef"
      @send="handleSend"
      @send-file="handleSendFile"
      @send-image="handleSendImage"
    />

    <!-- 图片预览弹窗 -->
    <teleport to="body">
      <div
        v-if="previewImageUrl"
        class="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center cursor-pointer"
        @click="previewImageUrl = ''"
      >
        <img :src="previewImageUrl" class="max-w-[90vw] max-h-[90vh] object-contain rounded-lg" />
        <button class="absolute top-4 right-4 p-2 text-white/80 hover:text-white bg-black/30 rounded-full">
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
          </svg>
        </button>
      </div>
    </teleport>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useChatStore } from '@/stores/chat'
import { useUserStore } from '@/stores/user'
import MessageBubble from './MessageBubble.vue'
import ChatInput from './ChatInput.vue'

const chatStore = useChatStore()
const userStore = useUserStore()
const currentUserId = computed(() => userStore.userInfo?.userId)
const messagesContainer = ref(null)
const chatInputRef = ref(null)
const showScrollButton = ref(false)
const previewImageUrl = ref('')

const conversation = computed(() => chatStore.currentConversation)

const headerAvatar = computed(() => {
  const conv = conversation.value
  if (!conv || conv.type === 'group') return ''
  const other = (conv.members || []).find(m => String(m.id) !== String(currentUserId.value))
  if (other) return chatStore.getUserInfo(other.id).avatar
  return ''
})

// 平台端用户头像颜色
const avatarColors = {
  u1: 'bg-blue-500',
  u2: 'bg-primary-500',
  u3: 'bg-teal-500',
  u4: 'bg-orange-500',
  u5: 'bg-pink-500',
  u6: 'bg-indigo-500',
  u7: 'bg-rose-500',
  other: 'bg-slate-500'
}

// APP端用户头像颜色（使用绿色系区分）
const appAvatarColors = {
  u1: 'bg-green-500',
  u2: 'bg-emerald-500',
  u3: 'bg-teal-500',
  u4: 'bg-lime-500',
  u5: 'bg-emerald-600',
  u6: 'bg-green-600',
  u7: 'bg-teal-600',
  other: 'bg-emerald-500'
}

function getAvatarColor(senderId, senderType) {
  // senderType=2 表示APP端用户，使用绿色系颜色
  if (senderType === 2) {
    return appAvatarColors[senderId] || appAvatarColors.other
  }
  return avatarColors[senderId] || avatarColors.other
}

function shouldShowDate(msg, index) {
  if (index === 0) return true
  const prevMsg = chatStore.currentMessages[index - 1]
  if (!prevMsg) return false
  const curr = new Date(msg.time)
  const prev = new Date(prevMsg.time)
  if (isNaN(curr.getTime()) || isNaN(prev.getTime())) return true
  return (curr - prev) > 5 * 60 * 1000
}

function formatMessageDate(timeStr) {
  if (!timeStr) return ''
  const date = new Date(timeStr)
  if (isNaN(date.getTime())) return ''
  const now = new Date()
  const isToday = date.toDateString() === now.toDateString()
  const time = date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })
  if (isToday) return time
  const yesterday = new Date(now)
  yesterday.setDate(yesterday.getDate() - 1)
  if (date.toDateString() === yesterday.toDateString()) return `昨天 ${time}`
  return `${date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' })} ${time}`
}

function handleSend(content) {
  chatStore.sendNewMessage(content)
  nextTick(() => scrollToBottom())
}

function handleSendFile(fileData) {
  chatStore.sendFileMessage(fileData.file)
  nextTick(() => scrollToBottom())
}

function handleSendImage(imageData) {
  chatStore.sendImageMessage(imageData.file)
  nextTick(() => scrollToBottom())
}

function handleReply(message) {
  chatStore.setReplyTo(message)
  chatInputRef.value?.focus()
}

function handleDeleteMessage(messageId) {
  chatStore.deleteMessage(messageId)
}

function scrollToBottom() {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

function handleScroll() {
  if (!messagesContainer.value) return
  const { scrollTop, scrollHeight, clientHeight } = messagesContainer.value
  showScrollButton.value = scrollHeight - scrollTop - clientHeight > 200

  // 滚动到顶部时加载更多历史消息
  if (scrollTop < 50 && !chatStore.messageLoading) {
    const prevHeight = scrollHeight
    chatStore.loadMoreMessages().then((loaded) => {
      if (loaded) {
        nextTick(() => {
          if (messagesContainer.value) {
            messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight - prevHeight
          }
        })
      }
    })
  }
}

watch(
  () => chatStore.currentMessages.length,
  () => nextTick(() => scrollToBottom())
)

watch(
  () => chatStore.currentConversationId,
  () => nextTick(() => scrollToBottom()),
  { flush: 'post' }
)

// 消息加载完成后滚动到底部
watch(
  () => chatStore.messageLoading,
  (loading, oldLoading) => {
    if (oldLoading && !loading) {
      nextTick(() => scrollToBottom())
    }
  }
)
</script>

<style scoped>
.fade-up-enter-active,
.fade-up-leave-active {
  transition: all 0.2s ease;
}
.fade-up-enter-from,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(8px);
}
</style>
